import React, { useState } from 'react';
import { Workflow, FileSearch, Target, Users, ArrowRight, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { consultingServices, methodology } from '../data/mock';
import LeadCaptureModal from './LeadCaptureModal';

const iconMap = {
  Workflow,
  FileSearch,
  Target,
  Users
};

const ConsultingSection = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  return (
    <>
    <section id="consulting" className="py-24 bg-zinc-950 relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">Consultoria BIM</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">Implementação BIM Estratégica</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transforme sua empresa com uma estratégia BIM completa e personalizada.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {consultingServices.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className="bg-black border-zinc-800 hover:border-red-500/50 transition-all duration-300 hover:scale-105 group"
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-red-600/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600/20 transition-colors">
                    <Icon className="text-red-500" size={28} />
                  </div>
                  <CardTitle className="text-white text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-400">{service.description}</CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Methodology Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Nossa Metodologia</h3>
            <p className="text-gray-300">Processo estruturado em 4 etapas para garantir o sucesso da implementação BIM</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {methodology.map((step, index) => (
              <div key={step.step} className="relative">
                <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300 h-full">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">{step.step}</span>
                      </div>
                      <Badge variant="outline" className="border-red-500/30 text-red-400">
                        {step.duration}
                      </Badge>
                    </div>
                    <CardTitle className="text-white text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400 text-base">{step.description}</CardDescription>
                  </CardContent>
                </Card>
                {index < methodology.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="text-red-500" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* CTA para conhecer as etapas */}
          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => window.location.href = '/metodologia'}
            >
              Conhecer Cada Etapa em Detalhes
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>

        {/* Sub-Products Section */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Subprodutos da Implementação</h3>
            <p className="text-gray-300">Documentação técnica e recursos desenvolvidos durante o processo</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { title: 'BIM Mandate', desc: 'Requisitos e diretrizes BIM', icon: 'FileText' },
              { title: 'BEP', desc: 'Plano de Execução BIM', icon: 'ClipboardList' },
              { title: 'Templates', desc: 'Modelos padronizados', icon: 'Layout' },
              { title: 'Bibliotecas', desc: 'Famílias customizadas', icon: 'Library' },
              { title: 'Protocolos', desc: 'Diretrizes de modelagem', icon: 'Settings' },
              { title: 'Workflows', desc: 'Processos colaborativos', icon: 'Network' }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-black border border-zinc-800 rounded-lg p-6 hover:border-red-500/50 transition-all duration-300"
              >
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-red-900/20 to-black border border-red-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Pronto para implementar BIM na sua empresa?
            </h3>
            <p className="text-gray-300 mb-6">
              Agende uma reunião gratuita para discutir as necessidades da sua organização.
            </p>
            <a
              href="https://meet.brevo.com/alexander-justi/reuniao-video-conferencia"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
              data-testid="consulting-schedule-meet-btn"
            >
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white group"
              >
                <CalendarClock className="mr-2" size={20} />
                Agendar reunião online
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </a>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setIsLeadModalOpen(true)}
                className="text-sm text-gray-400 hover:text-gray-200 underline underline-offset-4 transition-colors"
                data-testid="diagnostic-button"
              >
                ou solicite um diagnóstico gratuito por formulário
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    <LeadCaptureModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} />
    </>
  );
};

export default ConsultingSection;